
import {
  Component, Prop, Watch, Mixins, Inject,
} from 'vue-property-decorator';
import { InputValidationRules } from 'vuetify';
import ExpandableInput from '@/features/common/components/ExpandableInput.vue';
import Tooltip from '@/features/common/components/Tooltip.vue';
import SaveToBook from '@/features/shipment/components/SaveToBook.vue';
import { CityProvinceLookupMixin } from '@/features/shipment/components/mixins';
import { CountryInfo } from '@/features/common/types';
import { SoldToAddress, SoldToEntityMatch, SoldToState } from '@/features/shipment/types';
import {
  requiredRules,
  phoneNumberInvalidRules,
  emailRules,
  minCharactersRules,
  contactOrCompanyRule,
  isProvinceRequired,
  getPostalCodeRules,
  stripUnoCharFromPostalCode,
} from '@/features/common/helpers/validationRules';
import { safeListedCountryCodes } from '@/features/common/raw';
import UndoClearMixin from '@/features/common/mixins/undoClearMixin';
import {
  rulesIfAttempted as rulesIfAttemptedFn,
  CreateShipmentValidationState,
} from '@/features/shipment/helpers/createShipmentValidation';

@Component({
  components: {
    ExpandableInput,
    Tooltip,
    SaveToBook,
  },
})
export default class SoldToPanel extends Mixins(CityProvinceLookupMixin, UndoClearMixin) {
  @Inject({
    from: 'createShipmentValidationState',
    default: () => ({ attempted: true }),
  })
  private createShipmentValidationState!: CreateShipmentValidationState;

  @Prop({ required: true }) value!: SoldToState;

  @Prop({ required: true }) shipToCountryCode!: string;

  @Prop({ required: true }) saveCallback!: (param: unknown) => Promise<unknown>;

  private localEntityMatch: SoldToEntityMatch = 'same';

  private soldToData: SoldToAddress = {
    contact: '',
    company: '',
    phone: '',
    addr1: '',
    addr2: '',
    addr3: '',
    countryCode: '',
    postalCode: '',
    province: '',
    city: '',
    emails: [],
  };

  private soldToPostalBlurred = false;

  private requiredRules = requiredRules;

  private contactOrCompanyRule = contactOrCompanyRule;

  private minCharactersRules = minCharactersRules;

  private emailRules = emailRules;

  private phoneNumberInvalidRules = phoneNumberInvalidRules;

  // -- Computed --

  get soldToAddressText(): string {
    return [this.soldToData.addr1, this.soldToData.addr2, this.soldToData.addr3]
      .filter(Boolean).join('');
  }

  set soldToAddressText(val: string) {
    const text = val || '';
    this.soldToData.addr1 = text.substring(0, 35);
    this.soldToData.addr2 = text.substring(35, 70);
    this.soldToData.addr3 = text.substring(70, 105);
  }

  get soldToEmail(): string {
    return this.soldToData.emails ? this.soldToData.emails[0] : '';
  }

  set soldToEmail(val: string) {
    this.soldToData = {
      ...this.soldToData,
      emails: val ? [val] : [],
    };
  }

  get countryItems(): Array<CountryInfo> {
    const { countries } = this.$store.state;
    return countries || [];
  }

  get soldToCurrentCountry(): CountryInfo | undefined {
    return this.$store.getters.getCountryFromCode(this.soldToData.countryCode);
  }

  get soldToPostalCodeRules(): InputValidationRules {
    if (!this.soldToCurrentCountry) return this.requiredRules;
    return getPostalCodeRules(this.soldToCurrentCountry);
  }

  get soldToProvinceLabel(): string {
    const label = this.soldToData.countryCode === 'US'
      ? this.$t('labels.state') as string
      : this.$t('labels.province') as string;
    return this.soldToProvinceRules.length ? `${label} *` : label;
  }

  get soldToProvinceRules(): InputValidationRules {
    return isProvinceRequired(this.soldToData.countryCode) ? this.requiredRules : [];
  }

  get showSoldToCityProvince(): boolean {
    if (safeListedCountryCodes.includes(this.soldToData.countryCode)) {
      return !!(this.soldToData.countryCode
        && this.soldToData.postalCode
        && (this.soldToPostalBlurred || this.soldToData.city));
    }
    return !!this.soldToData.countryCode;
  }

  // -- Watchers --

  @Watch('value', { immediate: true, deep: true })
  private syncFromProp(newVal: SoldToState): void {
    if (!newVal) return;
    this.localEntityMatch = newVal.entityMatch;
    this.soldToData = { ...newVal.address };
    if (newVal.address.postalCode) this.soldToPostalBlurred = true;
  }

  @Watch('soldToData.contact')
  private onSoldToContactChange(): void {
    this.$emit('revalidate', 'soldto-company');
  }

  @Watch('soldToData.company')
  private onSoldToCompanyChange(): void {
    this.$emit('revalidate', 'soldto-contact-name');
  }

  @Watch('shipToCountryCode', { immediate: true })
  private syncCountry(newCode: string): void {
    if (!newCode) return;
    if (this.soldToData.countryCode !== newCode) {
      this.soldToData.countryCode = newCode;
      this.soldToData.postalCode = '';
      this.soldToData.city = '';
      this.soldToData.province = '';
      this.soldToPostalBlurred = false;
      this.emitUpdate();
    }
  }

  // -- Methods --

  private rulesIfAttempted(rules?: InputValidationRules): InputValidationRules {
    return rulesIfAttemptedFn(rules, this.createShipmentValidationState.attempted);
  }

  private setEntityMatch(match: SoldToEntityMatch): void {
    this.localEntityMatch = match;
    this.emitUpdate();
  }

  private emitUpdate(): void {
    const state: SoldToState = {
      entityMatch: this.localEntityMatch,
      address: { ...this.soldToData },
    };
    this.$emit('input', state);
  }

  private handleSoldToPostalCodeBlur(): void {
    this.soldToData.postalCode = stripUnoCharFromPostalCode(this.soldToData.postalCode);
    if (this.soldToData.postalCode) {
      this.soldToData.postalCode = this.soldToData.postalCode.toUpperCase();
    }
    this.soldToPostalBlurred = true;
    this.emitUpdate();
    this.handleAutofillCityProvince();
  }

  private handleAutofillCityProvince(): void {
    const fill = (city: string, province: string): void => {
      this.soldToData.city = city || this.soldToData.city;
      this.soldToData.province = province || this.soldToData.province;
      this.emitUpdate();
    };

    this.autofillCityProvince(fill, 'soldToData');
  }

  private isRequiredFieldsFilled(): boolean {
    const {
      contact, company, phone, addr1, countryCode, city, province,
    } = this.soldToData;

    const hasContactOrCompany = !!(contact?.trim() || company?.trim());
    const isFilled = hasContactOrCompany
      && !!phone?.trim() && !!addr1?.trim() && !!countryCode?.trim() && !!city?.trim();

    return isProvinceRequired(countryCode) ? isFilled && !!province?.trim() : isFilled;
  }

  private async validateSoldToFields(): Promise<boolean> {
    this.createShipmentValidationState.attempted = true;
    await this.$nextTick();
    return this.isRequiredFieldsFilled();
  }
}
